import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import ConfirmationDialog from '../components/ConfirmationDialog';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const createCondition = () => ({ attributeId: '', operator: '', value: '', proficiency: 1 });
const createCriteriaGroup = () => ({ logic: 'AND', conditions: [createCondition()] });

const isCriteriaGroup = (node) => Array.isArray(node?.conditions);

const getCriteriaConditions = (node) => (
  isCriteriaGroup(node)
    ? node.conditions.flatMap((condition) => getCriteriaConditions(condition))
    : [node]
);

const formatCriteria = (node) => {
  if (!isCriteriaGroup(node)) {
    return `${node.attributeId} (${node.operator}) ${node.value} (proficiency: ${node.proficiency}) `;
  }

  return `(${node.conditions.map(formatCriteria).join(` ${node.logic || 'AND'} `)})`;
};

const RoutingGroupsPage = () => {
  const [routingGroups, setRoutingGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [routingGroupsForm, setRoutingGroupsForm] = useState({
    instanceId: '',
    routingGroupId: '',
    displayName: '',
    criteria: createCriteriaGroup(),
    connectQueueId: '',
    active: true
  });
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingRoutingGroups, setLoadingRoutingGroups] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [queues, setQueues] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, routingGroup: null });


  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;
  const ROUTING_GROUPS_API = apiEndpoints.routingGroups;
  const ATTRIBUTES_VIEW_API = apiEndpoints.attributesView;
  const QUEUES_DROPDOWN_API = apiEndpoints.queues;
  const ROUTING_GROUP_REGISTRY_API = apiEndpoints.routingGroupRegistry; 

     // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    setResult(null);
    setError(null);
  };

  const handleRoutingGroupsFormChange = (e) => {
    const { name, value, checked, type } = e.target;

    setRoutingGroupsForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'instanceId') {
      setError(null);
      setResult(null);
      if (value) {
        fetchRoutingGroupList(value);
        fetchAttributeList(value);
        fetchQueueList(value);
      } else {
        setRoutingGroups([]);
        setAttributes([]);
        setQueues([]);
      }
    }
  };

  const updateCriteriaNode = (node, path, update) => {
    if (path.length === 0) return update(node);

    return {
      ...node,
      conditions: node.conditions.map((condition, index) => (
        index === path[0] ? updateCriteriaNode(condition, path.slice(1), update) : condition
      ))
    };
  };

  const handleCriteriaChange = (path, field, value) => {
    setRoutingGroupsForm(prev => ({
      ...prev,
      criteria: updateCriteriaNode(prev.criteria, path, (criterion) => ({
        ...criterion,
        [field]: value,
        ...(field === 'attributeId'
          ? {
              operator: Object.entries(attributes.find((attribute) => attribute.attributeId === value)?.allowedOperators || {})
                .some(([, allowed]) => allowed) ? '' : 'EQUALS',
              value: '',
              proficiency: 1
            }
          : {})
      }))
    }));
  };

  const handleCriteriaLogicChange = (path, e) => {
    setRoutingGroupsForm(prev => ({
      ...prev,
      criteria: updateCriteriaNode(prev.criteria, path, (group) => ({ ...group, logic: e.target.value }))
    }));
  };

  const addCriteria = (path, nodeType) => {
    setRoutingGroupsForm(prev => ({
      ...prev,
      criteria: updateCriteriaNode(prev.criteria, path, (group) => ({
        ...group,
        conditions: [...group.conditions, nodeType === 'group' ? createCriteriaGroup() : createCondition()]
      }))
    }));
  };

  const removeCriteria = (path) => {
    if (path.length === 0) return;

    setRoutingGroupsForm(prev => ({
      ...prev,
      criteria: updateCriteriaNode(prev.criteria, path.slice(0, -1), (group) => ({
        ...group,
        conditions: group.conditions.filter((_, index) => index !== path[path.length - 1])
      }))
    }));
  };

   // Fetch agents for a specific instance
  const fetchRoutingGroupList = async (instanceId) => {
    setLoadingRoutingGroups(true);
    
    setRoutingGroups([]);
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiService.post(ROUTING_GROUPS_API, {
        instanceId: instanceId,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setRoutingGroups(response.data.result?.routingGroups || []);
        setPage(1);
      } else {
        console.error('Failed to load routingGroups:', response.data.message);
        setError('Failed to load routingGroups');
      }
    } catch (err) {
      console.error('Error fetching routingGroups:', err);
      setError('Error fetching routingGroups: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingRoutingGroups(false);
    }
  };

  const fetchAttributeList = async (instanceId) => {
    setLoadingAttributes(true);
    setAttributes([]);

    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(ATTRIBUTES_VIEW_API, {
        instance_id: instanceId,
        user_role: user?.role || ''
      });

      if (response.data.status === 'ok') {
        setAttributes(response.data.result?.attributes || []);
      } else {
        setError('Failed to load attributes');
      }
    } catch (err) {
      setError('Error fetching attributes: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingAttributes(false);
    }
  };

  const fetchQueueList = async (instanceId) => {
    setLoadingQueues(true);
    setQueues([]);

    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(QUEUES_DROPDOWN_API, {
        instance_id: instanceId,
        user_role: user?.role || ''
      });

      if (response.data.status === 'ok') {
        setQueues(response.data.queues || []);
      } else {
        setError('Failed to load queues');
      }
    } catch (err) {
      setError('Error fetching queues: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingQueues(false);
    }
  };

 const handleRoutingGroupsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(ROUTING_GROUP_REGISTRY_API, {
        ...routingGroupsForm,
        user_role: user?.role || ''
      });

      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to create routing group');
      }

      await fetchRoutingGroupList(routingGroupsForm.instanceId);
      setRoutingGroupsForm(prev => ({
        ...prev,
        routingGroupId: '',
        displayName: '',
        criteria: createCriteriaGroup(),
        connectQueueId: '',
        active: true
      }));
      setResult({ status: 'ok', message: 'Application routing group created successfully.' });
    } catch (err) {
        setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create routingGroup');
    } finally {
        setLoading(false);
    }
  };

  const loadRoutingGroup = (routingGroup) => {
    setRoutingGroupsForm((prev) => ({
      instanceId: prev.instanceId,
      routingGroupId: routingGroup.routingGroupId || '',
      displayName: routingGroup.displayName || '',
      criteria: routingGroup.criteria
        ? JSON.parse(JSON.stringify(routingGroup.criteria))
        : createCriteriaGroup(),
      connectQueueId: routingGroup.connectQueueId || '',
      active: routingGroup.active !== false
    }));
    setError(null);
    setResult({ message: `Loaded routing group '${routingGroup.routingGroupId}'.` });
  };

  const handleDeleteRoutingGroup = (routingGroup) => {
    setConfirmDialog({ isOpen: true, routingGroup });
  };

  const confirmDeleteRoutingGroup = async () => {
    const routingGroup = confirmDialog.routingGroup;
    setConfirmDialog({ isOpen: false, routingGroup: null });
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(`${apiEndpoints.deleteRoutingGroup}/${routingGroup.routingGroupId}`, {
        instanceId: routingGroupsForm.instanceId,
        routingGroupId: routingGroup.routingGroupId,
        user_role: user?.role || ''
      });

      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to delete routing group');
      }

      await fetchRoutingGroupList(routingGroupsForm.instanceId);
      setResult({ status: 'ok', message: 'Routing group deleted successfully.' });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete routing group');
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteRoutingGroup = () => {
    setConfirmDialog({ isOpen: false, routingGroup: null });
  };

  const getRoutingGroupPreview = () => ({
    ...routingGroupsForm,
    user_role: JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
  });

  const formatQueue = (queueId) => {
    if (!queueId) return '-';
    const queue = queues.find((item) => item.id === queueId);
    return queue ? `${queue.name} (${queue.id})` : queueId;
  };

  const getSortValue = (attribute, key) => {
    if (key === 'criteria') {
      return formatCriteria(attribute.criteria || createCriteriaGroup()).toLowerCase();
    }
    return String(attribute[key] ?? '').toLowerCase();
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredRoutingGroups = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return routingGroups;
    return routingGroups.filter((attribute) => {
      return [
        attribute.routingGroupId,
        attribute.displayName,
        attribute.connectQueueId,
        attribute.active ? 'active' : 'inactive',
        formatCriteria(attribute.criteria || createCriteriaGroup()),
        ...getCriteriaConditions(attribute.criteria || createCriteriaGroup())
          .flatMap((criterion) => [criterion.attributeId, criterion.operator, criterion.value, criterion.proficiency])
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [routingGroups, searchTerm]);

  const sortedRoutingGroups = React.useMemo(() => {
    if (!sortKey) return filteredRoutingGroups;
    const copy = [...filteredRoutingGroups];
    copy.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRoutingGroups, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRoutingGroups.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRoutingGroups = sortedRoutingGroups.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };  

  const renderCriteriaGroup = (group, groupPath) => (
    <div className="criteria-group" key={groupPath.join('-') || 'root'}>
      <div className="form-group criteria-logic-control">
        <label htmlFor={`criteriaLogic-${groupPath.join('-') || 'root'}`}>
          {groupPath.length === 0 ? 'Logical Relationship' : 'Nested Relationship'}
        </label>
        <select
          id={`criteriaLogic-${groupPath.join('-') || 'root'}`}
          value={group.logic}
          onChange={(e) => handleCriteriaLogicChange(groupPath, e)}
          disabled={group.conditions.length < 2}
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      </div>
      <div className={groupPath.length === 0 ? '' : 'criteria-nested-group'}>
        {group.conditions.map((condition, index) => {
          const conditionPath = [...groupPath, index];

          if (isCriteriaGroup(condition)) {
            return (
              <div className="criteria-group-wrapper" key={conditionPath.join('-')}>
                {renderCriteriaGroup(condition, conditionPath)}
                <button
                  type="button"
                  className="btn btn-secondary allowed-value-remove"
                  onClick={() => removeCriteria(conditionPath)}
                >
                  Remove Group
                </button>
              </div>
            );
          }

          const selectedAttribute = attributes.find(
            (attribute) => attribute.attributeId === condition.attributeId
          );
          const configuredOperators = Object.entries(selectedAttribute?.allowedOperators || {})
            .filter(([, allowed]) => allowed)
            .map(([operator]) => operator);
          const allowedOperators = configuredOperators.length > 0 ? configuredOperators : ['EQUALS'];

          return (
            <div className="allowed-value-row" key={conditionPath.join('-')}>
              <div className="form-group">
                <label htmlFor={`criteriaAttributeId-${conditionPath.join('-')}`}>Attribute ID</label>
                <select
                  id={`criteriaAttributeId-${conditionPath.join('-')}`}
                  value={condition.attributeId}
                  onChange={(e) => handleCriteriaChange(conditionPath, 'attributeId', e.target.value)}
                  required
                >
                  <option value="">
                    {loadingAttributes ? 'Loading attributes...' : 'Select an attribute'}
                  </option>
                  {attributes.filter((attribute) => attribute.active !== false).map((attribute) => (
                    <option key={attribute.attributeId} value={attribute.attributeId}>
                      {attribute.attributeId}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor={`criteriaOperator-${conditionPath.join('-')}`}>Operator</label>
                <select
                  id={`criteriaOperator-${conditionPath.join('-')}`}
                  value={condition.operator}
                  onChange={(e) => handleCriteriaChange(conditionPath, 'operator', e.target.value)}
                  required
                >
                  <option value="">Select an operator</option>
                  {allowedOperators.map((operator) => (
                    <option key={operator} value={operator}>{operator}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor={`criteriaValue-${conditionPath.join('-')}`}>Value</label>
                <select
                  id={`criteriaValue-${conditionPath.join('-')}`}
                  value={condition.value}
                  onChange={(e) => handleCriteriaChange(conditionPath, 'value', e.target.value)}
                  disabled={!condition.attributeId || !condition.operator || loadingAttributes}
                  required
                >
                  <option value="">Select a value</option>
                  {(selectedAttribute?.allowedValues || []).map((allowedValue) => (
                    <option key={allowedValue.value} value={allowedValue.value}>
                      {allowedValue.value}{allowedValue.displayName ? ` (${allowedValue.displayName})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor={`criteriaProficiency-${conditionPath.join('-')}`}>Proficiency</label>
                <select
                  type="number"
                  id={`criteriaProficiency-${conditionPath.join('-')}`}
                  value={condition.proficiency}
                  onChange={(e) => handleCriteriaChange(conditionPath, 'proficiency', parseInt(e.target.value, 10))}
                  required
                >
                  <option value="">Select proficiency</option>
                  {[1, 2, 3, 4, 5].map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <button
                  type="button"
                  className="btn btn-secondary allowed-value-remove"
                  onClick={() => removeCriteria(conditionPath)}
                  disabled={group.conditions.length === 1}
                >
                  Remove
              </button>
            </div>
          );
        })}
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={() => addCriteria(groupPath, 'condition')}>
          Add Condition
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => addCriteria(groupPath, 'group')}>
          Add Criteria Group
        </button>
      </div>
    </div>
  );

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };
    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  return (
      <div className="setup-page">
       <div className="page-header">
        <div>
            <h1>View Routing Groups</h1>
              <p className="page-subtitle">Manage Routing Groups for Connect Contact and Application attributes</p>
        </div>
        <Link to="/routing-admin" className="btn btn-secondary">
          ← Back to Routing Administration
         </Link>
       </div>

      {/* Environment Selector */}
      <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
        >
          {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
        </select>
        <span style={{
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
          {selectedEnvironment}
        </span>
      </div>
        <div className="form-container">
            <form onSubmit={handleRoutingGroupsSubmit} className="setup-form">
                <div className="form-section">
                <h3>New Routing Group</h3>
                    <div className="form-group">
                        <label htmlFor="instanceId">Instance *</label>
                        <select
                        id="instanceId"
                        name="instanceId"
                        value={routingGroupsForm.instanceId}
                        onChange={handleRoutingGroupsFormChange}
                        required
                        disabled={loadingInstances}
                        >
                        <option value="">
                            {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                        </option>
                        {instances.map((instance) => (
                            <option key={instance.id} value={instance.id}>
                            {instance.name} ({instance.status})
                            </option>
                        ))}
                        </select>
                        <small>Select an Amazon Connect instance</small>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="routingGroupId">Routing Group ID *</label>
                        <input
                          id="routingGroupId"
                          name="routingGroupId"
                          type="text"
                          value={routingGroupsForm.routingGroupId}
                          onChange={handleRoutingGroupsFormChange}
                          required
                          maxLength={256}
                          placeholder="EMPLOYEE_TYPE"
                        />
                        <small>Stable identifier used by routing criteria for the routing group</small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="displayName">Display Name *</label>
                        <input
                          id="displayName"
                          name="displayName"
                          type="text"
                          value={routingGroupsForm.displayName}
                          onChange={handleRoutingGroupsFormChange}
                          required
                          maxLength={256}
                          placeholder="employeeType"
                        />
                      </div>
                    </div>
                    <fieldset className="allowed-values-fieldset">
                      <legend>Criteria *</legend>
                      {renderCriteriaGroup(routingGroupsForm.criteria, [])}
                    </fieldset>
                    <div className="form-group">
                        <label htmlFor="connectQueueId">Queue ID *</label>
                        <select
                          id="connectQueueId"
                          name="connectQueueId"
                          value={routingGroupsForm.connectQueueId}
                          onChange={handleRoutingGroupsFormChange}
                          required
                          disabled={!routingGroupsForm.instanceId || loadingQueues}
                        >
                          <option value="">
                            {loadingQueues ? 'Loading queues...' : 'Select a queue...'}
                          </option>
                          {queues.map((queue) => (
                            <option key={queue.id} value={queue.id}>
                              {queue.name} ({queue.id})
                            </option>
                          ))}
                        </select>
                        <small>Connect queue ID for the routing group</small>
                      </div>
                    <div className="form-group attribute-active-control">
                      <label htmlFor="active">
                        <input
                          id="active"
                          name="active"
                          type="checkbox"
                          checked={routingGroupsForm.active}
                          onChange={handleRoutingGroupsFormChange}
                        />
                        Active
                      </label>
                    </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowJsonPreview(true)}
                  >
                    Preview JSON
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || loadingRoutingGroups}
                  >
                    {loading ? 'Creating...' : 'Create Routing Group'}
                  </button>
                   {/* <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCompile}
                    disabled={!routingGroupsForm.instanceId || loadingCompile}
                  >
                    {loadingCompile ? 'Compiling...' : 'Compile'}
                  </button> */}
              </div>
            </form>
        </div>                

        {showJsonPreview && (
          <div
            className="json-preview-overlay"
            role="presentation"
            onClick={() => setShowJsonPreview(false)}
          >
            <div
              className="json-preview-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="json-preview-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="json-preview-header">
                <h3 id="json-preview-title">Routing Group JSON Preview</h3>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowJsonPreview(false)}
                  aria-label="Close JSON preview"
                >
                  Close
                </button>
              </div>
              <pre className="json-preview-content">
                {JSON.stringify(getRoutingGroupPreview(), null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* {showCompiledPreview && (
          <div
            className="json-preview-overlay"
            role="presentation"
            onClick={() => setShowCompiledPreview(false)}
          >
            <div
              className="json-preview-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="compiled-preview-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="json-preview-header">
                <h3 id="compiled-preview-title">AWS Connect Routing Criteria</h3>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCompiledPreview(false)}
                  aria-label="Close compiled criteria preview"
                >
                  Close
                </button>
              </div>
              <pre className="json-preview-content">
                {JSON.stringify(compiledCriteria, null, 2)}
              </pre>
            </div>
          </div>
        )} */}

        {(loading || loadingRoutingGroups) && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your request...</p>
          </div>
        )}

        {routingGroups.length > 0 && (
          <div className="table-format-list">
            <div className="table-format-list-header">
              <h3>Routing Groups ({sortedRoutingGroups.length}{sortedRoutingGroups.length !== routingGroups.length ? ` of ${routingGroups.length}` : ''})</h3>
              <div className="table-format-list-controls">
                <input
                  type="text"
                  className="table-format-search-input"
                  placeholder="Search Routing Groups..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
                <div className="page-size-selector">
                  <label htmlFor="page_size">Rows per page:</label>
                  <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('routingGroupId')}>ID{sortIndicator('routingGroupId')}</th>
                  <th className="sortable" onClick={() => handleSort('displayName')}>Display Name{sortIndicator('displayName')}</th>
                  <th className="sortable" onClick={() => handleSort('criteria')}>Criteria{sortIndicator('criteria')}</th>
                  <th className="sortable" onClick={() => handleSort('connectQueueId')}>Connect Queue ID{sortIndicator('connectQueueId')}</th>
                  <th className="sortable" onClick={() => handleSort('active')}>Status{sortIndicator('active')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRoutingGroups.map((routingGroup) => (
                  <tr key={routingGroup.routingGroupId}>
                    <td>{routingGroup.routingGroupId || '-'}</td>
                    <td>{routingGroup.displayName || '-'}</td>
                    <td>
                      {routingGroup.criteria ? formatCriteria(routingGroup.criteria) : '-'}
                    </td>
                    <td>{formatQueue(routingGroup.connectQueueId)}</td>
                    <td>{routingGroup.active ? 'Active' : 'Inactive'}</td>
                    <td>
                      <button type="button" className="btn btn-secondary" onClick={() => loadRoutingGroup(routingGroup)}>
                        Load
                      </button>
                      <button type="button" className="btn btn-danger" onClick={() => handleDeleteRoutingGroup(routingGroup)} style={{marginLeft: '4px'}}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
          </div>
        )}

        {!loading && routingGroups.length === 0 && (
          <div className="attribute-list">
            <h3>Routing Groups ({routingGroups.length})</h3>
            <p>No routing groups found.</p>
          </div>
        )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          <p>{result.message}</p>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Routing Group"
        message={`Are you sure you want to delete routing group '${confirmDialog.routingGroup?.routingGroupId}'? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDanger={true}
        onConfirm={confirmDeleteRoutingGroup}
        onCancel={cancelDeleteRoutingGroup}
      />

    </div>
    );
};

export default RoutingGroupsPage;